import React, { useEffect, useState } from 'react';
import Modal from 'components/UI/Modal';
import InputComponent from 'components/UI/Input';
import SelectComponent from 'components/UI/Select';
import { Main } from 'styles/themeColors';
import { FormGroup } from '@mui/material';
import { TEXT_LABELS, USERS_BUSINESS_UNIT_CONSTANTS } from 'constants/usersBusinessUnit';
import { GENERAL_CONSTANTS } from 'constants/general';
import { BUSINESS_UNIT_USER_ROLES, OFMP_ADMIN, USER_ROLE_MAPPER, VENDOR_ADMIN_ROLE } from 'constants/user';
import WarningBanner from 'components/UI/WarningBanner';
import { useAuth } from 'lib/Auth';
import Vendor from 'data/Vendor';
import { hasOfmpAdminProfile, Role } from 'data/User';
import { BusinessUnit } from 'data/BusinessUnit';
import * as SessionStorage from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';
import { PermissionCode } from 'data/UserPermissions';
import { useReadPermission } from 'hooks/useUserRoles';
import { getScope } from 'utils/businessUnit';

interface InviteUserForm {
    name: string;
    email: string;
    vendor: string;
    businessUnit: string;
    userRole: any;
    ginId?: string;
}

interface InviteUserModalProps {
    show: boolean;
    onClose: () => void;
    onSendInvitation: () => void;
    inviteForm: InviteUserForm;
    onInputChange: (field: string, value: any) => void;
    businessUnitOptions?: Array<{ value: string; id: string }>;
    roleOptions?: Array<{ value: string; id: string }>;
    isLoading?: boolean;
    errors?: any;
    businessProfileId?: number;
    isEdit?: boolean;
    alreadyInvitedRole?: string;
    setAlreadyInvitedRole?: (role: string) => void;
    viewMode?: string
    selectedVendor?: Vendor;
    selectedBusinessUnit?: BusinessUnit;
    salesOrderFeatureFlag?: boolean;
}

const BusinessUnitInviteUserModal: React.FC<InviteUserModalProps> = ({
    show,
    onClose,
    onSendInvitation,
    inviteForm,
    onInputChange,
    roleOptions = BUSINESS_UNIT_USER_ROLES,
    isLoading = false,
    errors = {},
    isEdit = false,
    alreadyInvitedRole = '',
    setAlreadyInvitedRole,
    viewMode = '',
    selectedVendor,
    salesOrderFeatureFlag = false,
}) => {

    const { token, user } = useAuth();
    const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [vendorsLoading, setVendorsLoading] = useState(false);
    const { readOnly: vendorReadAdminPermission } = useReadPermission(PermissionCode.INVITE_VENDOR_ADMINS);

    // Include Vendor Admin and OFMP Admin roles when in "All Users" view mode
    // In "Admins" view mode, only show Admin and Vendor Admin roles
    const availableRoles = React.useMemo(() => {
        if (viewMode === GENERAL_CONSTANTS.ADMINS) {
            return [
                ...BUSINESS_UNIT_USER_ROLES,
                ...VENDOR_ADMIN_ROLE,
                ...OFMP_ADMIN
            ];
        }
        if (viewMode === GENERAL_CONSTANTS.ALL_USERS) {
            return [
                ...BUSINESS_UNIT_USER_ROLES,
                ...(!vendorReadAdminPermission ? VENDOR_ADMIN_ROLE : []),
                ...(hasOfmpAdminProfile(user) ? OFMP_ADMIN : [])
            ];
        }
        return roleOptions;
    }, [viewMode, vendorReadAdminPermission, user, roleOptions]);

    // Role-based access control helpers based on SELECTED role in the form
    const selectedRoleIsAdmin = inviteForm.userRole?.id === Role.Admin ||
        inviteForm.userRole?.id === Role.SuperAdmin;
    const selectedRoleIsVendorAdmin = inviteForm.userRole?.id === Role.VendorAdmin;

    // Load vendors from session storage when in "All Users" or "Admins" view mode
    useEffect(() => {
        if (viewMode === GENERAL_CONSTANTS.ALL_USERS || viewMode === GENERAL_CONSTANTS.ADMINS) {
            setVendorsLoading(true);
            try {
                const vendorsFromStorage = SessionStorage.getItems(STORAGE_KEYS.VENDORS);
                setVendors(vendorsFromStorage || []);
            } catch (error) {
                console.error("Error loading vendors from session storage:", error);
                setVendors([]);
            } finally {
                setVendorsLoading(false);
            }
        }
    }, [viewMode]);

    // Fetch business units when vendor is selected and in "All Users" view mode
    useEffect(() => {
        if (viewMode === GENERAL_CONSTANTS.ALL_USERS || viewMode === GENERAL_CONSTANTS.ADMINS) {
            // Clear business unit selection when vendor changes (but not in edit mode to preserve prepopulated value)
            if (inviteForm.businessUnit && !isEdit) {
                onInputChange('businessUnit' as any, '');
            }

            if (inviteForm.vendor) {
                const selectedVendorObj = vendors.find(v => v.id === parseInt(inviteForm.vendor));
                if (selectedVendorObj && (!selectedRoleIsVendorAdmin && !selectedRoleIsAdmin)) {
                    const organizations = SessionStorage.getItems(STORAGE_KEYS.BUSINESS_UNITS);
                    const userOrganizations = organizations.filter((x: any) => x.vendorId === parseInt(inviteForm.vendor));
                    setBusinessUnits(userOrganizations);
                }
            } else {
                // Clear business units when no vendor is selected
                setBusinessUnits([]);
            }
        }
    }, [inviteForm.vendor, inviteForm.userRole, viewMode, token, vendors]);

    // Clear vendor and business unit when selected role changes to Admin
    useEffect(() => {
        if (selectedRoleIsAdmin) {
            // Clear vendor and business unit when Admin role is selected
            if (inviteForm.vendor) {
                onInputChange('vendor' as any, '');
            }
            if (inviteForm.businessUnit) {
                onInputChange('businessUnit' as any, '');
            }
        }
    }, [selectedRoleIsAdmin]);

    // Clear business unit when selected role changes to Vendor Admin
    useEffect(() => {
        if (selectedRoleIsVendorAdmin) {
            // Clear business unit when Vendor Admin role is selected
            if (inviteForm.businessUnit) {
                onInputChange('businessUnit' as any, '');
            }
        }
    }, [selectedRoleIsVendorAdmin]);

    return (
        <Modal
            showModal={show}
            onClose={onClose}
            onClick={onSendInvitation}
            title={isEdit ? USERS_BUSINESS_UNIT_CONSTANTS.UPDATE_INVITATION : USERS_BUSINESS_UNIT_CONSTANTS.INVITE_USER}
            primaryButtonText={isEdit ? GENERAL_CONSTANTS.UPDATE : USERS_BUSINESS_UNIT_CONSTANTS.SEND_INVITATION}
            ghostButtonText={GENERAL_CONSTANTS.BACK}
            primaryButtonColor={Main.brandDark}
            color={Main.black}
            borderColor={Main.lightGray}
            width="440px"
            left="38%"
            showCloseIcon
            disabled={isLoading}
        >
            {
                alreadyInvitedRole && (
                    <WarningBanner
                        message={alreadyInvitedRole in USER_ROLE_MAPPER ?
                            USERS_BUSINESS_UNIT_CONSTANTS.ALREADY_INVITED
                                .replace('{role}', USER_ROLE_MAPPER[alreadyInvitedRole])
                                .replace('{scope}', getScope(alreadyInvitedRole)) :
                            alreadyInvitedRole
                        }
                        type='error'
                        margin='0 0 16px 0'
                    />
                )
            }

            <FormGroup style={{ marginBottom: '16px' }}>
                <InputComponent
                    id="invite-user-name"
                    labelName={TEXT_LABELS.NAME}
                    value={inviteForm.name}
                    placeholder={GENERAL_CONSTANTS.ENTER_FULL_NAME}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onInputChange('name' as any, e.target.value)
                    }
                    displayAsterisk={true}
                    isOptional={false}
                    isInvalid={!!errors.name}
                    errorMessage={errors.name}
                    disabled={isLoading}
                />
            </FormGroup>

            <FormGroup style={{ marginBottom: '16px' }}>
                <InputComponent
                    id="invite-user-email"
                    labelName={TEXT_LABELS.CONTACT_EMAIL}
                    type="email"
                    value={inviteForm.email}
                    placeholder={GENERAL_CONSTANTS.ENTER_EMAIL}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onInputChange('email' as any, e.target.value)
                        setAlreadyInvitedRole && setAlreadyInvitedRole('');
                    }}
                    displayAsterisk={true}
                    isOptional={false}
                    isInvalid={!!errors.email}
                    errorMessage={errors.email}
                    disabled={isLoading || isEdit}
                />
            </FormGroup>

            {
                salesOrderFeatureFlag &&
                <FormGroup style={{ marginBottom: '16px' }}>
                    <InputComponent
                        id="invite-user-ginId"
                        labelName={TEXT_LABELS.GIN_ID}
                        value={inviteForm.ginId}
                        placeholder={GENERAL_CONSTANTS.ENTER_GIN}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            onInputChange('ginId' as any, e.target.value)
                        }}
                        choiceOptional
                        productRedesignFlag
                        disabled={isLoading}
                    />
                </FormGroup>
            }

            <FormGroup style={{ marginBottom: '16px' }}>
                <SelectComponent
                    label={TEXT_LABELS.USER_ROLE}
                    labelPosition="top"
                    options={availableRoles}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onInputChange('userRole' as any, { id: e.target.id, value: e.target.value })
                    }
                    value={inviteForm.userRole.value}
                    defaultValue={GENERAL_CONSTANTS.SELECT_ROLE}
                    displayAsterisk={true}
                    isOptional={false}
                    isInvalid={!!errors.userRole}
                    isRedLable={!!errors.userRole}
                    errorMessage={errors.userRole}
                    disabled={isLoading}
                    placeholder={GENERAL_CONSTANTS.SELECT_ROLE}
                    errroLabelPostion="bottom"
                />
            </FormGroup>

            {
                (viewMode === GENERAL_CONSTANTS.ALL_USERS || viewMode === GENERAL_CONSTANTS.ADMINS) &&
                <FormGroup style={{ marginBottom: '16px' }}>
                    <SelectComponent
                        label={TEXT_LABELS.VENDOR}
                        labelPosition="top"
                        options={vendors?.map(vendor => ({ id: vendor.id, value: vendor.name } as any))}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onInputChange('vendor' as any, e.target.id)
                        }
                        value={vendors.find(v => v.id === parseInt(inviteForm.vendor))?.name || ''}
                        defaultValue={GENERAL_CONSTANTS.SELECT_VENDOR}
                        isInvalid={!!errors.vendor}
                        isRedLable={!!errors.vendor}
                        errorMessage={errors.vendor}
                        disabled={isLoading || selectedRoleIsAdmin || vendorsLoading}
                        placeholder={GENERAL_CONSTANTS.SELECT_VENDOR}
                        errroLabelPostion="bottom"
                    />
                </FormGroup>
            }

            {
                (viewMode === GENERAL_CONSTANTS.ALL_USERS || viewMode === GENERAL_CONSTANTS.ADMINS) && <FormGroup style={{ marginBottom: '16px' }}>
                    <SelectComponent
                        label={TEXT_LABELS.BUSINESS_UNIT}
                        labelPosition="top"
                        options={businessUnits?.map(unit => ({ id: unit.id, value: unit.name } as any))}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onInputChange('businessUnit' as any, e.target.id)
                        }
                        value={businessUnits.find(unit => unit.id === parseInt(inviteForm.businessUnit))?.name || ''}
                        defaultValue={GENERAL_CONSTANTS.SELECT_BUSINESS_UNIT}
                        isInvalid={!!errors.businessUnit}
                        isRedLable={!!errors.businessUnit}
                        errorMessage={errors.businessUnit}
                        disabled={isLoading || selectedRoleIsAdmin || selectedRoleIsVendorAdmin}
                        placeholder={GENERAL_CONSTANTS.SELECT_BUSINESS_UNIT}
                        errroLabelPostion="bottom"
                    />
                </FormGroup>
            }

        </Modal>
    );
};

export default React.memo(BusinessUnitInviteUserModal);